import { useEffect, useRef, useState } from "preact/hooks"
import { api } from "../lib/api"
import {
	fullSrcSet,
	fullUrl,
	originalUrl,
	recordingUrl,
	videoPosterUrl,
} from "../lib/cloudinary"
import {
	type PhotoEdits,
	defaultEdits,
	isNoopEdits,
	renderEditedImage,
} from "../lib/imageEdits"
import { friendlyArtDate } from "../lib/time"
import { type UploadedAsset, uploadToCloudinary } from "../lib/uploads"
import type { ArtDate, Artwork, Asset, Role } from "../lib/types"
import { ArtDateInput } from "./ArtDateInput"
import { useConfirm } from "./Confirm"
import { PhotoEditor } from "./PhotoEditor"
import { Spinner } from "./Spinner"

interface ArtworkViewProps {
	artworkId: number
	navigate: (path: string) => void
	cloudName: string
}

export function ArtworkView({
	artworkId,
	navigate,
	cloudName,
}: ArtworkViewProps) {
	const confirm = useConfirm()
	const [artwork, setArtwork] = useState<Artwork | null>(null)
	const [role, setRole] = useState<Role | null>(null)
	const [error, setError] = useState("")
	const [editing, setEditing] = useState(false)
	const [title, setTitle] = useState("")
	const [note, setNote] = useState("")
	const [madeDate, setMadeDate] = useState<ArtDate>({
		year: null,
		month: null,
		day: null,
	})
	const [savingEdit, setSavingEdit] = useState(false)
	const [addingPhoto, setAddingPhoto] = useState(false)
	const [addingRecording, setAddingRecording] = useState(false)
	const [deletingAssetId, setDeletingAssetId] = useState<number | null>(null)
	const [deletingArtwork, setDeletingArtwork] = useState(false)
	// Re-editing an uploaded photo: which asset's original is being fetched,
	// the open editor's source (the untransformed Cloudinary original), and
	// which asset a just-applied edit is being saved over.
	const [fetchingOriginalId, setFetchingOriginalId] = useState<number | null>(
		null,
	)
	const [photoEdit, setPhotoEdit] = useState<{
		assetId: number
		source: File
	} | null>(null)
	const [savingEditId, setSavingEditId] = useState<number | null>(null)
	// Track which photo images have finished loading to avoid jarring layout
	// shifts — show a placeholder in the same space until onLoad fires.
	const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set())
	// Sibling artworks in the box, for prev/next navigation (see the effect
	// below `load`). Touch start point for the swipe gesture on the photo area.
	const [siblings, setSiblings] = useState<Artwork[] | null>(null)
	const touchStartRef = useRef<{ x: number; y: number } | null>(null)

	const markImageLoaded = (assetId: number) => {
		setLoadedImages((prev) => {
			if (prev.has(assetId)) return prev
			const next = new Set(prev)
			next.add(assetId)
			return next
		})
	}

	const load = () => {
		api
			.getArtwork(artworkId)
			.then((detail) => {
				setArtwork(detail.artwork)
				setRole(detail.role)
				setTitle(detail.artwork.title)
				setNote(detail.artwork.note)
				setMadeDate(detail.artwork.date)
			})
			.catch((err) =>
				setError(err instanceof Error ? err.message : "Failed to load artwork"),
			)
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: `load` is stable enough for this component's lifetime
	useEffect(load, [artworkId])

	// Sibling list for prev/next navigation, in the box's existing display
	// order (date desc, undated last) — used as-is, never re-sorted. This is
	// progressive enhancement only: a slow or failed fetch must never block
	// or break the main artwork view, so failures are swallowed and the nav
	// UI (see `position` below) just stays hidden.
	useEffect(() => {
		setSiblings(null)
		if (!artwork) return
		let cancelled = false
		api
			.listArtworks(artwork.box_id)
			.then((list) => {
				if (!cancelled) setSiblings(list)
			})
			.catch(() => {})
		return () => {
			cancelled = true
		}
	}, [artwork?.box_id])

	const siblingIndex =
		artwork && siblings ? siblings.findIndex((a) => a.id === artwork.id) : -1
	const prevId =
		siblings && siblingIndex > 0 ? siblings[siblingIndex - 1].id : null
	const nextId =
		siblings && siblingIndex >= 0 && siblingIndex < siblings.length - 1
			? siblings[siblingIndex + 1].id
			: null
	const position =
		siblings && siblingIndex >= 0
			? `${siblingIndex + 1} of ${siblings.length}`
			: null

	// Arrow-key prev/next through the box (mirrored by a swipe handler on the
	// photo area further down). Both must stay inert while inline-editing
	// (the title/note/date form, `editing`) or while the photo editor
	// overlay is open (`photoEdit`), and arrows must not hijack typing in any
	// form control — otherwise navigating away would silently discard
	// in-progress edits. Regression guard: do not remove these checks.
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (editing || photoEdit) return
			if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return
			const target = e.target as HTMLElement | null
			if (target) {
				const tag = target.tagName
				if (
					tag === "INPUT" ||
					tag === "TEXTAREA" ||
					tag === "SELECT" ||
					target.isContentEditable
				) {
					return
				}
			}
			if (e.key === "ArrowLeft" && prevId) navigate(`/artworks/${prevId}`)
			if (e.key === "ArrowRight" && nextId) navigate(`/artworks/${nextId}`)
		}
		document.addEventListener("keydown", onKeyDown)
		return () => document.removeEventListener("keydown", onKeyDown)
	}, [editing, photoEdit, prevId, nextId, navigate])

	if (error) {
		return (
			<div class="container py-4">
				<div class="alert alert-danger">{error}</div>
			</div>
		)
	}
	if (!artwork) {
		return (
			<div class="container py-4">
				<Spinner />
			</div>
		)
	}

	const isOwner = role === "owner"
	const photos = artwork.assets.filter((a) => a.kind === "photo")
	const recordingAsset = artwork.assets.find((a) => a.kind === "recording")

	const saveEdit = async (e: Event) => {
		e.preventDefault()
		setSavingEdit(true)
		setError("")
		try {
			await api.updateArtwork(artworkId, {
				title: title.trim(),
				note: note.trim(),
				date: madeDate,
			})
			setEditing(false)
			load()
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to save changes")
		} finally {
			setSavingEdit(false)
		}
	}

	const onAddPhoto = async (e: Event) => {
		const input = e.target as HTMLInputElement
		const file = input.files?.[0]
		input.value = ""
		if (!file) return
		setAddingPhoto(true)
		setError("")
		try {
			const sign = await api.signUpload(artwork.box_id, "image")
			const uploaded = await uploadToCloudinary(file, sign, "image")
			await api.addAsset(artworkId, { kind: "photo", ...uploaded })
			load()
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to add photo")
		} finally {
			setAddingPhoto(false)
		}
	}

	const onAddRecording = async (e: Event) => {
		const input = e.target as HTMLInputElement
		const file = input.files?.[0]
		input.value = ""
		if (!file) return
		setAddingRecording(true)
		setError("")
		try {
			const sign = await api.signUpload(
				artwork.box_id,
				"video",
				file.type.startsWith("video/") ? "video" : "audio",
			)
			const uploaded = await uploadToCloudinary(file, sign, "video")
			await api.addAsset(artworkId, { kind: "recording", ...uploaded })
			load()
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to add recording")
		} finally {
			setAddingRecording(false)
		}
	}

	const deleteAsset = async (assetId: number) => {
		if (
			!(await confirm("Delete this photo/recording? This can't be undone."))
		) {
			return
		}
		setDeletingAssetId(assetId)
		setError("")
		try {
			await api.deleteAsset(artworkId, assetId)
			load()
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to delete")
		} finally {
			setDeletingAssetId(null)
		}
	}

	const startPhotoEdit = async (photo: Asset) => {
		setFetchingOriginalId(photo.id)
		setError("")
		try {
			const res = await fetch(
				originalUrl(cloudName, photo.cloudinary_public_id),
			)
			if (!res.ok) {
				throw new Error(`Could not download the photo (${res.status})`)
			}
			const blob = await res.blob()
			setPhotoEdit({
				assetId: photo.id,
				source: new File([blob], "photo", { type: blob.type }),
			})
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Could not load photo for editing",
			)
		} finally {
			setFetchingOriginalId(null)
		}
	}

	// Unlike the capture flow, edits here are destructive: the edited render
	// replaces the stored photo and the previous pixels are destroyed, so a
	// later re-edit starts from this result (no un-crop). Add-then-delete
	// order matters — the server refuses to delete an artwork's last photo.
	const applyPhotoEdit = async (edits: PhotoEdits) => {
		if (!photoEdit) return
		const { assetId, source } = photoEdit
		setPhotoEdit(null)
		if (isNoopEdits(edits)) return
		setSavingEditId(assetId)
		setError("")
		let uploaded: UploadedAsset | null = null
		let added = false
		try {
			const rendered = await renderEditedImage(source, edits)
			const sign = await api.signUpload(artwork.box_id, "image")
			uploaded = await uploadToCloudinary(rendered.blob, sign, "image")
			await api.addAsset(artworkId, { kind: "photo", ...uploaded })
			added = true
			await api.deleteAsset(artworkId, assetId)
			load()
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to edit photo")
			if (uploaded && !added) {
				// The render reached Cloudinary but never became an asset row;
				// destroy it so it doesn't orphan.
				api
					.destroyUpload(artwork.box_id, uploaded.cloudinary_public_id, "image")
					.catch(() => {})
			}
			if (added) {
				// The edited photo was added but the original's removal failed —
				// reload so the UI shows both and the stale one can be deleted.
				load()
			}
		} finally {
			setSavingEditId(null)
		}
	}

	const deleteArtwork = async () => {
		if (
			!(await confirm(
				"Delete this artwork and all its photos/recordings? This can't be undone.",
			))
		) {
			return
		}
		setDeletingArtwork(true)
		setError("")
		try {
			await api.deleteArtwork(artworkId)
			navigate(`/boxes/${artwork.box_id}`)
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to delete artwork")
			setDeletingArtwork(false)
		}
	}

	// Swipe on the photo area: left goes to next, right goes to prev. Same
	// regression guard as the arrow-key handler above — must stay inert
	// while inline-editing or while the photo editor overlay is open, and
	// only fires when the horizontal movement clearly dominates so it never
	// hijacks vertical page scrolling.
	const onPhotoTouchStart = (e: TouchEvent) => {
		if (editing || photoEdit) {
			touchStartRef.current = null
			return
		}
		const t = e.touches[0]
		touchStartRef.current = t ? { x: t.clientX, y: t.clientY } : null
	}

	const onPhotoTouchEnd = (e: TouchEvent) => {
		const start = touchStartRef.current
		touchStartRef.current = null
		if (!start || editing || photoEdit) return
		const t = e.changedTouches[0]
		if (!t) return
		const dx = t.clientX - start.x
		const dy = t.clientY - start.y
		if (Math.abs(dx) <= 50 || Math.abs(dx) <= Math.abs(dy) * 2) return
		if (dx < 0 && nextId) navigate(`/artworks/${nextId}`)
		if (dx > 0 && prevId) navigate(`/artworks/${prevId}`)
	}

	return (
		<div class="container py-3 py-sm-4" style={{ maxWidth: "720px" }}>
			<div class="d-flex align-items-center justify-content-between mb-3">
				<a
					href={`/boxes/${artwork.box_id}`}
					class="btn btn-outline-secondary btn-sm"
				>
					<i class="bi bi-arrow-left" aria-hidden="true" />
					<span>Back</span>
				</a>
				{position && (
					<div class="d-flex align-items-center gap-1">
						{prevId ? (
							<a
								href={`/artworks/${prevId}`}
								class="btn btn-outline-secondary btn-sm d-inline-flex align-items-center justify-content-center"
								aria-label="Previous artwork"
							>
								<i class="bi bi-chevron-left" aria-hidden="true" />
							</a>
						) : (
							<span
								class="btn btn-outline-secondary btn-sm invisible d-inline-flex align-items-center justify-content-center"
								aria-hidden="true"
							>
								<i class="bi bi-chevron-left" aria-hidden="true" />
							</span>
						)}
						<span
							class="text-body-secondary small text-center"
							style={{ minWidth: "4.5em" }}
						>
							{position}
						</span>
						{nextId ? (
							<a
								href={`/artworks/${nextId}`}
								class="btn btn-outline-secondary btn-sm d-inline-flex align-items-center justify-content-center"
								aria-label="Next artwork"
							>
								<i class="bi bi-chevron-right" aria-hidden="true" />
							</a>
						) : (
							<span
								class="btn btn-outline-secondary btn-sm invisible d-inline-flex align-items-center justify-content-center"
								aria-hidden="true"
							>
								<i class="bi bi-chevron-right" aria-hidden="true" />
							</span>
						)}
					</div>
				)}
				{isOwner && (
					<button
						type="button"
						class="btn btn-outline-danger btn-sm"
						onClick={deleteArtwork}
						disabled={deletingArtwork}
					>
						{deletingArtwork ? (
							<Spinner size={16} />
						) : (
							<i class="bi bi-trash" aria-hidden="true" />
						)}
						<span>Delete</span>
					</button>
				)}
			</div>

			{error && <div class="alert alert-danger">{error}</div>}

			{editing ? (
				<form onSubmit={saveEdit} class="vstack gap-3 mb-3">
					<div>
						<label class="form-label fw-semibold" for="edit-title">
							Title
						</label>
						<input
							id="edit-title"
							type="text"
							class="form-control"
							value={title}
							onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
						/>
					</div>
					<div>
						<label class="form-label fw-semibold" for="edit-note">
							Note
						</label>
						<textarea
							id="edit-note"
							class="form-control"
							rows={3}
							value={note}
							onInput={(e) => setNote((e.target as HTMLTextAreaElement).value)}
						/>
					</div>
					<div>
						<label class="form-label fw-semibold" for="edit-made-year">
							Date made
						</label>
						<ArtDateInput
							idPrefix="edit-made"
							value={madeDate}
							onChange={setMadeDate}
						/>
					</div>
					<div class="d-flex gap-2">
						<button
							type="submit"
							class="btn btn-primary flex-fill"
							disabled={savingEdit}
						>
							{savingEdit ? (
								<Spinner size={16} />
							) : (
								<i class="bi bi-check-lg" aria-hidden="true" />
							)}
							<span>Save</span>
						</button>
						<button
							type="button"
							class="btn btn-outline-secondary flex-fill"
							onClick={() => setEditing(false)}
						>
							Cancel
						</button>
					</div>
				</form>
			) : (
				<div class="mb-3">
					<div class="d-flex align-items-start justify-content-between gap-2">
						<div class="text-break" style={{ minWidth: 0 }}>
							<h1 class="h4 mb-1">{artwork.title || "Untitled"}</h1>
							<div class="text-body-secondary small mb-2">
								{friendlyArtDate(artwork.date)}
							</div>
							{artwork.note && (
								<p class="mb-0" style={{ whiteSpace: "pre-wrap" }}>
									{artwork.note}
								</p>
							)}
						</div>
						{isOwner && (
							// styles.sass makes .btn extend .hstack, which sets align-self: stretch.
							// Pin this button to the top so it doesn't grow with a long note.
							<button
								type="button"
								class="btn btn-outline-secondary flex-shrink-0 align-self-start d-inline-flex align-items-center"
								onClick={() => setEditing(true)}
							>
								<i class="bi bi-pencil" aria-hidden="true" />
								<span>Edit</span>
							</button>
						)}
					</div>
				</div>
			)}

			<div
				class="vstack gap-2 mb-3"
				onTouchStart={onPhotoTouchStart}
				onTouchEnd={onPhotoTouchEnd}
			>
				{photos.map((photo, index) => {
					const isLoaded = loadedImages.has(photo.id)
					return (
						<div class="position-relative" key={photo.id}>
							{/* Reserve space while the image loads to prevent layout
							    shift — at the photo's own aspect ratio when we know it. */}
							{!isLoaded && (
								<div
									class="bg-body-secondary rounded d-flex align-items-center justify-content-center"
									style={
										photo.width > 0 && photo.height > 0
											? { aspectRatio: `${photo.width} / ${photo.height}` }
											: { minHeight: "250px" }
									}
								>
									<Spinner size={32} />
								</div>
							)}
							{/* srcset lets the browser fetch the smallest render that
							    still looks sharp on its screen; the first photo is the
							    one the viewer is waiting on, so it gets priority. */}
							<img
								src={fullUrl(cloudName, photo.cloudinary_public_id)}
								srcset={fullSrcSet(cloudName, photo.cloudinary_public_id)}
								sizes="(min-width: 768px) 688px, 100vw"
								alt={artwork.title}
								decoding="async"
								fetchPriority={index === 0 ? "high" : "auto"}
								class={`w-100 rounded${isLoaded ? "" : " d-none"}`}
								onLoad={() => markImageLoaded(photo.id)}
							/>
							{savingEditId === photo.id && (
								<div class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-25 rounded">
									<Spinner size={32} />
								</div>
							)}
							{isOwner && (
								<button
									type="button"
									class="btn btn-secondary position-absolute top-0 start-0 m-2 rounded-circle d-flex align-items-center justify-content-center"
									style={{ width: "44px", height: "44px", padding: 0 }}
									onClick={() => startPhotoEdit(photo)}
									disabled={
										fetchingOriginalId !== null || savingEditId !== null
									}
									aria-label="Edit photo"
								>
									{fetchingOriginalId === photo.id ? (
										<Spinner size={16} />
									) : (
										<i class="bi bi-pencil" aria-hidden="true" />
									)}
								</button>
							)}
							{isOwner && photos.length > 1 && (
								<button
									type="button"
									class="btn btn-danger position-absolute top-0 end-0 m-2 rounded-circle d-flex align-items-center justify-content-center"
									style={{ width: "44px", height: "44px", padding: 0 }}
									onClick={() => deleteAsset(photo.id)}
									disabled={deletingAssetId === photo.id}
									aria-label="Delete photo"
								>
									{deletingAssetId === photo.id ? (
										<Spinner size={16} />
									) : (
										<i class="bi bi-x-lg" aria-hidden="true" />
									)}
								</button>
							)}
						</div>
					)
				})}
			</div>

			{recordingAsset && (
				<div class="mb-4">
					<div class="d-flex align-items-center justify-content-between mb-1">
						<span class="fw-semibold">Artist's demo</span>
						{isOwner && (
							<button
								type="button"
								class="btn btn-sm btn-outline-danger"
								onClick={() => deleteAsset(recordingAsset.id)}
								disabled={deletingAssetId === recordingAsset.id}
							>
								{deletingAssetId === recordingAsset.id ? (
									<Spinner size={16} />
								) : (
									<i class="bi bi-trash" aria-hidden="true" />
								)}
								<span>Remove</span>
							</button>
						)}
					</div>
					{recordingAsset.width > 0 ? (
						// poster + preload="metadata": show the first frame right away
						// (a small jpg) instead of pulling down the whole video just to
						// paint something.
						<video
							src={recordingUrl(cloudName, recordingAsset)}
							poster={videoPosterUrl(
								cloudName,
								recordingAsset.cloudinary_public_id,
							)}
							preload="metadata"
							controls
							playsInline
							class="w-100 rounded bg-dark"
						/>
					) : (
						<audio
							src={recordingUrl(cloudName, recordingAsset)}
							controls
							class="w-100"
						/>
					)}
				</div>
			)}

			{/* Every "add" control lives here, below the photos and the demo, so
			    the page reads as content first and actions last. */}
			{isOwner && (
				<div class="btn-group w-100 mb-4" role="group" aria-label="Add media">
					<label class="btn btn-outline-secondary flex-fill">
						<i class="bi bi-image-fill" aria-hidden="true" />
						<span>{addingPhoto ? "Uploading…" : "Add another photo"}</span>
						<input
							type="file"
							accept="image/*"
							capture="environment"
							class="d-none"
							onChange={onAddPhoto}
							disabled={addingPhoto}
						/>
					</label>
					{!recordingAsset && (
						<label class="btn btn-outline-secondary flex-fill">
							<i class="bi bi-mic-fill" aria-hidden="true" />
							<span>{addingRecording ? "Uploading…" : "Add a demo"}</span>
							<input
								type="file"
								accept="audio/*,video/*"
								class="d-none"
								onChange={onAddRecording}
								disabled={addingRecording}
							/>
						</label>
					)}
				</div>
			)}

			{photoEdit && (
				<PhotoEditor
					file={photoEdit.source}
					initialEdits={defaultEdits()}
					onApply={applyPhotoEdit}
					onCancel={() => setPhotoEdit(null)}
				/>
			)}
		</div>
	)
}
